/**
 * 前端版本管理工具
 * 用于检测前后端版本不一致的情况，提示用户刷新页面
 */

// 版本号：每次构建时更新（可以通过构建脚本自动更新）
// 格式：YYYYMMDD.HHmm 或者使用 git commit hash
export const FRONTEND_VERSION = "20260120.0024";

// 存储首次加载时获取的后端版本
let initialBackendVersion = null;

// 是否已经提示过用户刷新
let hasPromptedRefresh = false;

/**
 * 检查版本一致性
 * 对比前端代码中的版本号与后端返回的版本号
 * @param {string} backendVersion - 从响应头获取的后端版本
 * @returns {boolean} 版本是否一致
 */
export const checkVersion = (backendVersion) => {
    if (!backendVersion) return true; // 如果后端没有返回版本，跳过检查

    // 直接对比前端代码版本与后端版本
    // 如果前端版本比后端版本旧，说明用户需要刷新页面获取新代码
    if (FRONTEND_VERSION !== backendVersion) {
        console.warn(`[VersionCheck] 版本不一致! 前端: ${FRONTEND_VERSION}, 后端: ${backendVersion}`);
        return false;
    }

    return true;
};

/**
 * 提示用户刷新页面
 * 使用 confirm 对话框，用户可以选择立即刷新或稍后刷新
 */
export const promptRefresh = () => {
    // 避免重复提示
    if (hasPromptedRefresh) return;
    hasPromptedRefresh = true;

    // 延迟提示，避免在 API 调用中途弹出
    setTimeout(() => {
        const shouldRefresh = window.confirm("检测到系统已更新，为确保功能正常，建议刷新页面。\n\n点击「确定」立即刷新，点击「取消」稍后刷新。");

        if (shouldRefresh) {
            window.location.reload();
        } else {
            // 用户选择稍后刷新，5分钟后重置提示状态
            setTimeout(
                () => {
                    hasPromptedRefresh = false;
                },
                5 * 60 * 1000,
            );
        }
    }, 100);
};

/**
 * 处理 API 响应中的版本头
 * 用于在 axios 响应拦截器中调用
 * @param {Object} response - axios 响应对象
 */
export const handleVersionHeader = (response) => {
    // axios 的 headers 可能是 AxiosHeaders 对象，需要用 get() 或直接访问
    // HTTP 头在传输时会被转换为小写
    let backendVersion = null;

    if (response.headers) {
        // 尝试多种方式获取响应头
        if (typeof response.headers.get === "function") {
            backendVersion = response.headers.get("x-frontend-version");
        } else {
            backendVersion = response.headers["x-frontend-version"];
        }
    }

    // 开发环境下输出调试信息
    if (process.env.NODE_ENV === "development" && backendVersion) {
        console.log(`[VersionCheck] 收到后端版本: ${backendVersion}`);
    }

    if (backendVersion && !checkVersion(backendVersion)) {
        promptRefresh();
    }
};

/**
 * 获取当前前端版本
 */
export const getFrontendVersion = () => FRONTEND_VERSION;

/**
 * 重置版本检查状态（用于测试）
 */
export const resetVersionCheck = () => {
    initialBackendVersion = null;
    hasPromptedRefresh = false;
};

export default {
    FRONTEND_VERSION,
    checkVersion,
    promptRefresh,
    handleVersionHeader,
    getFrontendVersion,
    resetVersionCheck,
};
